import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import Axios from '@/shared/api/axios';
import { setIdolId, setCurrentIdol, clearSession } from './userSlice';

// 取得錢包資訊 (8-1)
export const fetchWalletInfo = createAsyncThunk(
  'wallet/fetchInfo',
  async (idolId, { rejectWithValue }) => {
    try {
      const response = await Axios.get('/wallet/info', {
        params: {
          idol_id: idolId
        }
      });

      if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data?.status_code);
      }
    } catch (error) {
      return rejectWithValue(error.httpErrorCode);
    }
  }
);

// 建立充值訂單 (8-2)
export const createDeposit = createAsyncThunk(
  'wallet/createDeposit',
  async ({ idolId, amount }, { rejectWithValue }) => {
    try {
      const response = await Axios.post('/wallet/deposit/create', {
        amount: parseFloat(amount)
      }, {
        params: {
          idol_id: idolId
        }
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        return response.data.data;
      } else {
        return rejectWithValue(response.data?.status_code);
      }
    } catch (error) {
      return rejectWithValue(error.httpErrorCode);
    }
  }
);

// 建立提領訂單 (8-3)
export const createWithdraw = createAsyncThunk(
  'wallet/createWithdraw',
  async ({ idolId, amount, wallet_address, code }, { rejectWithValue }) => {
    try {
      const response = await Axios.post('/wallet/withdraw/create', {
        amount: parseFloat(amount),
        wallet_address,
        code
      }, {
        params: {
          idol_id: idolId
        }
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        return response.data.data;
      } else {
        return rejectWithValue(response.data?.status_code);
      }
    } catch (error) {
      return rejectWithValue(error.httpErrorCode);
    }
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  // 金額一律以 null 表示「還沒拿到」，不給 '0.00' 這種看起來像真餘額的預設值：
  // 使用者會拿著假餘額去解鎖／訂閱／提領，而 parseFloat(undefined) 更會一路變成畫面上的 ₣NaN。
  // 消費端請先判斷 null（未載入）再顯示。
  initialState: {
    balance: null,
    frozen_balance: null,
    total_deposited: null,
    total_withdrawn: null,
    loading: false
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalletInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWalletInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance;
        state.frozen_balance = action.payload.frozen_balance;
        state.total_deposited = action.payload.total_deposited;
        state.total_withdrawn = action.payload.total_withdrawn;
      })
      // 錯誤不進 store：各呼叫端用 dispatch(...).unwrap().catch() 接自己這一次的結果，
      // 存在 store 裡會讓錯誤活過該次操作，被下一個頁面誤讀成自己的錯誤
      .addCase(fetchWalletInfo.rejected, (state) => {
        state.loading = false;
      })
      // 餘額是 per-idol 的（fetchWalletInfo(idolId)），換偶像等於換一個錢包。
      // 登出、SYSTEM_1004 清 token、後台認證重導、刪除當前偶像後自動切換，全都會走這些
      // action，而且全都是 SPA 導向、不會重新載入頁面——不在這裡歸零，下一個身分會先看到
      // 上一個身分的餘額，扣款 modal 還會拿它算「扣款後餘額」並開放確認鍵。
      // setCurrentIdol（切換偶像）、clearSession（登出、帳號停權、SYSTEM_1004）同理。
      // addMatcher 必須排在所有 addCase 之後（RTK builder 規定）
      .addMatcher(isAnyOf(setIdolId, setCurrentIdol, clearSession), (state) => {
        state.balance = null;
        state.frozen_balance = null;
        state.total_deposited = null;
        state.total_withdrawn = null;
      });
  }
});

export default walletSlice.reducer;
